import { useMemo, useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { CalendarClock, MapPin, MessageSquare, Check } from "lucide-react";

const STATUS = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-green-100 text-green-800",
  in_progress: "bg-indigo-100 text-indigo-800",
};

const parseLocal = (s) => {
  const [y, m, d] = String(s).split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};
const fmtKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export function UpcomingCalendar({ bookings = [], onConfirm, onCheckin, onMessage }) {
  const byDate = useMemo(() => {
    const todayKey = fmtKey(new Date());
    const m = {};
    bookings
      .filter((b) => ["pending", "confirmed", "in_progress"].includes(b.status) && String(b.date) >= todayKey)
      .forEach((b) => { (m[b.date] = m[b.date] || []).push(b); });
    return m;
  }, [bookings]);
  const bookedDays = useMemo(() => Object.keys(byDate).map(parseLocal), [byDate]);
  const [selected, setSelected] = useState(new Date());

  const dayList = (byDate[fmtKey(selected)] || []).sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));

  return (
    <div className="mt-6 grid lg:grid-cols-[auto_1fr] gap-6 bg-white border border-border rounded-sm p-6" data-testid="vendor-calendar">
      <div>
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-bold text-muted-foreground mb-3">
          <CalendarClock className="w-4 h-4" />Upcoming schedule
        </div>
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(d) => d && setSelected(d)}
          modifiers={{ booked: bookedDays }}
          modifiersClassNames={{ booked: "relative font-semibold text-primary after:content-[''] after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-1 after:h-1 after:rounded-full after:bg-primary aria-selected:after:bg-white" }}
          className="rounded-sm border border-border w-fit"
          data-testid="vendor-calendar-picker"
        />
      </div>
      <div className="min-w-0">
        <h3 className="font-display text-lg font-medium" data-testid="calendar-selected-date">
          {selected.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
        </h3>
        <p className="text-sm text-muted-foreground">{dayList.length} upcoming booking{dayList.length === 1 ? "" : "s"}</p>
        <div className="mt-3 space-y-2 max-h-[300px] overflow-auto pr-1">
          {dayList.length === 0 ? (
            <p className="text-sm text-muted-foreground py-10 text-center" data-testid="calendar-empty">No bookings on this day.</p>
          ) : dayList.map((b) => (
            <div key={b.id} className="border border-border rounded-sm p-3" data-testid={`cal-booking-${b.id}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium truncate">{b.service_title}</p>
                  <p className="text-xs text-muted-foreground truncate">{b.customer_name} · {b.start_time}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-sm font-medium shrink-0 ${STATUS[b.status] || "bg-neutral-100 text-neutral-700"}`}>{b.status}</span>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-2.5">
                {b.status === "pending" && onConfirm && (
                  <Button size="sm" className="rounded-sm h-7 px-2.5" onClick={() => onConfirm(b.id)} data-testid={`cal-confirm-${b.id}`}><Check className="w-3.5 h-3.5 mr-1" />Confirm</Button>
                )}
                {b.status === "confirmed" && onCheckin && (
                  <Button size="sm" className="rounded-sm h-7 px-2.5" onClick={() => onCheckin(b.id)} data-testid={`cal-checkin-${b.id}`}><MapPin className="w-3.5 h-3.5 mr-1" />Check in</Button>
                )}
                {onMessage && (
                  <Button size="sm" variant="outline" className="rounded-sm h-7 px-2.5" onClick={() => onMessage(b)} data-testid={`cal-message-${b.id}`}><MessageSquare className="w-3.5 h-3.5 mr-1" />Message</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
